import { ANALYTICS_GRAPH_END_POINT, ANALYTICS_METRIC_END_POINT, ARS_REPORT_CSV_END_POINT, GUIDE_LIVE_MODELS_END_POINT, SUMMARY_REPORT_CSV_END_POINT } from './../AnalyticsConstants';
import ServiceWrapper from "Services/ServiceWrapper";
import { TAnalyticsPayload } from "../analytics";
import AuthManager from "Common/AuthManager";
import AppSettings from 'Settings/AppSettings';
import { TResponseData } from 'Services/types';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;


let metricsAbortController: AbortController | null = null;
let graphAbortController: AbortController | null = null;

const doPostWithAbort = async (
  url: string,
  payload: any,
  options: any,
  token: string | null,
  abortControllerSetter: (controller: AbortController) => void,
  abortControllerGetter: () => AbortController | null
): Promise<TResponseData> => {
  if (!url) throw new Error("Url not found");

  let retObj: TResponseData = {};

  try {
    const currentController = abortControllerGetter();
    if (currentController) {
      currentController.abort();
    }
    const newController = new AbortController();
    abortControllerSetter(newController);

    const sanitizedPayload = JSON.parse(JSON.stringify(payload, (_, value) =>
      Number.isNaN(value) ? null : value
    ));

    const isFileUpload = options?.isFileUpload || false;
    let authString = token ? `Bearer ${token}` : "";

    const headersObj: HeadersInit = {
      Accept: "application/json",
      "Content-Type": isFileUpload ? "multipart/form-data" : "application/json",
      Authorization: authString,
      ...(options?.headers || {})
    };
    const formBody = isFileUpload ? payload : JSON.stringify(sanitizedPayload);

    const rawResponse = await fetch(url, {
      method: "POST",
      headers: headersObj,
      body: formBody,
      signal: newController.signal
    });
    const rawData = await rawResponse.text();
    const sanitizedResponseData = rawData.replace(/:\s*NaN/g, ': null');
    retObj.data = JSON.parse(sanitizedResponseData);
    retObj.errorfound = false;
  } catch (error) {
    retObj = { errorfound: true, errorbj: error, data: null };
  }

  return retObj;
};

export const fetchAllMetrics = async (payload: TAnalyticsPayload) => {
  try {
    const response = await doPostWithAbort(
      api_base_url + ANALYTICS_METRIC_END_POINT,
      payload,
      null,
      AuthManager.getGmsToken(),
      (controller) => { metricsAbortController = controller; },
      () => metricsAbortController
    );

    return response?.data;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }
    console.log(error);
    throw error;
  }
}

export const fetchAllGraphData = async (payload: TAnalyticsPayload) => {
  try {
    const response = await doPostWithAbort(
      api_base_url + ANALYTICS_GRAPH_END_POINT,
      payload,
      null,
      AuthManager.getGmsToken(),
      (controller) => { graphAbortController = controller; },
      () => graphAbortController
    );

    return response?.data;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }
    console.log(error);
    throw error;
  }
}

export const downloadSummaryReport = async (payload: TAnalyticsPayload) => {
  try {
    const reportPayload = { ...payload, domainURL: 'https://www.galco.com/' };
    const response = await ServiceWrapper.doPost(
      api_base_url + SUMMARY_REPORT_CSV_END_POINT,
      reportPayload,
      null,
      AuthManager.getGmsToken()
    );
    return response;
  } catch (error) {
    console.log(error);
    return null;
  }
}

export const downloadArsReport = async (payload: TAnalyticsPayload) => {
  try {
    const response = await ServiceWrapper.doPost(
      api_base_url + ARS_REPORT_CSV_END_POINT,
      payload,
      null,
      AuthManager.getGmsToken()
    );
    return response;
  } catch (error) {
    console.log(error);
    return null;
  }
}

export const fetchLiveGuideModels = async (
  industryId: string,
  merchantKey: string,
  dateRange: string[],
  timezone: string
) => {
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Request timeout')), 10000)
  );

  const token = AuthManager.getGmsToken();
  const url = `${api_base_url}${GUIDE_LIVE_MODELS_END_POINT}`;
  const fetchOptions: RequestInit = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : ''
    },
    body: JSON.stringify({ industry: industryId, channel: merchantKey, date_range: dateRange, timezone })
  };

  try {
    const rawResponse = await Promise.race([fetch(url, fetchOptions), timeoutPromise]);
    const data = await rawResponse.json();
    return { data, errorfound: false };
  } catch (error) {
    console.error('[fetchLiveGuideModels] error:', error);
    return { errorfound: true, errorbj: error, data: null };
  }
};



